import {
  CircleCheckMarkIcon,
  CircleCrossIcon,
  CircleInfoMessageIcon,
  CircleWarningIcon,
  XIcon,
} from '@icons/index.js';
import { UIButton, UIDiv, UILabel } from '@ui';

export class Toast extends UIDiv {
  static instance;

  constructor() {
    if (Toast.instance) {
      return Toast.instance;
    }

    super();
    this.setId('toast-container');
    this.setClass(
      'fixed top-5 right-5 z-[500] flex flex-col items-end gap-3 pointer-events-none',
    );
    document.body.appendChild(this.dom);
    this.toasts = [];

    Toast.instance = this;
  }

  show(message, description = null, type = 'info', duration = 3000) {
    if (!this.dom.parentNode) {
      document.body.appendChild(this.dom);
    }

    const toast = new UIDiv();
    toast.setClass(
      `flex items-center gap-5 p-3 rounded-lg shadow-lg transform translate-x-full pointer-events-auto transition-all duration-500 ease-in-out ${this.getTypeClass(type)}`,
    );

    const wrapperDiv = new UIDiv();
    wrapperDiv.setClass(
      'overflow-hidden transition-all duration-500 ease-in-out',
    );
    wrapperDiv.add(toast);

    const iconDiv = new UIDiv();
    iconDiv.setClass('flex-shrink-0');
    const icon = this.getIconForType(type);
    iconDiv.add(icon);
    toast.add(iconDiv);

    const contentDiv = new UIDiv();
    contentDiv.setClass('flex flex-col flex-grow');

    const messageLabel = new UILabel();
    messageLabel.setClass(
      `font-medium text-md ${this.getTextColorForType(type)}`,
    );
    messageLabel.setValue(message);
    contentDiv.add(messageLabel);

    if (description) {
      const descriptionLabel = new UILabel();
      descriptionLabel.setClass('text-gray-600 font-medium text-sm mt-1');
      descriptionLabel.setValue(description);
      contentDiv.add(descriptionLabel);
    }

    toast.add(contentDiv);

    const closeButton = new UIButton();
    closeButton.setClass(
      'ml-auto text-gray-400 hover:text-gray-600 focus:outline-none transition-colors duration-200',
    );
    const closeIcon = new XIcon({ size: 10, color: '#9CA3AF' });
    closeButton.add(closeIcon);
    closeButton.onClick(() => this.dismiss(wrapperDiv));
    toast.add(closeButton);

    this.add(wrapperDiv);

    wrapperDiv._toast = toast;
    wrapperDiv._originalHeight = null;

    requestAnimationFrame(() => {
      wrapperDiv._originalHeight = wrapperDiv.dom.offsetHeight + 'px';

      requestAnimationFrame(() => {
        toast.removeClass('translate-x-full');
        toast.addClass('translate-x-0');
      });
    });

    // Only set timeout if duration is not null, undefined, or 0
    if (duration && duration > 0) {
      const timeoutId = setTimeout(() => {
        this.dismiss(wrapperDiv);
      }, duration);
      wrapperDiv._timeoutId = timeoutId;
    }

    this.toasts.push(wrapperDiv);
    return wrapperDiv;
  }

  getTypeClass(type) {
    const typeClasses = {
      success: 'bg-white border-l-4 border-[var(--o-hive-success-120)]',
      error: 'bg-white border-l-4 border-[var(--o-hive-error-120)]',
      warning: 'bg-white border-l-4 border-[var(--o-hive-warning-120)]',
      info: 'bg-white border-l-4 border-[var(--o-hive-primary-100)]',
    };
    return typeClasses[type] || 'bg-white border-l-4 border-blue-500';
  }

  getIconForType(type) {
    const iconDiv = new UIDiv();
    iconDiv.setClass('w-10 h-10 rounded-full flex items-center justify-center');

    let icon;
    switch (type) {
      case 'success':
        iconDiv.addClass('bg-green-100');
        icon = new CircleCheckMarkIcon({
          size: 15,
          color: 'none',
          strokeColor: 'var(--o-hive-success-100)',
        });
        break;
      case 'error':
        iconDiv.addClass('bg-red-100');
        icon = new CircleCrossIcon({
          size: 15,
          color: 'none',
          strokeColor: 'var(--o-hive-error-120)',
        });
        break;
      case 'warning':
        iconDiv.addClass('bg-amber-100');
        icon = new CircleWarningIcon({
          size: 15,
          color: 'none',
          strokeColor: 'var(--o-hive-warning-120)',
        });
        break;
      case 'info':
      default:
        iconDiv.addClass('bg-blue-100');
        icon = new CircleInfoMessageIcon({
          size: 15,
          color: 'none',
          strokeColor: 'var(--o-hive-primary-100)',
        });
        break;
    }

    iconDiv.add(icon);
    return iconDiv;
  }

  getTextColorForType(type) {
    const textColors = {
      success: 'text-[var(--o-hive-success-120)]',
      error: 'text-[var(--o-hive-error-120)]',
      warning: 'text-[var(--o-hive-warning-120)]',
      info: 'text-[var(--o-hive-primary-100)]',
    };
    return textColors[type] || 'text-[var(--o-hive-primary-100)]';
  }

  success(message, description, duration) {
    return this.show(message, description, 'success', duration);
  }

  error(message, description, duration) {
    return this.show(message, description, 'error', duration);
  }

  warning(message, description, duration) {
    return this.show(message, description, 'warning', duration);
  }

  info(message, description, duration) {
    return this.show(message, description, 'info', duration);
  }

  dismiss(wrapperDiv) {
    if (!wrapperDiv || !wrapperDiv.dom) return;

    if (wrapperDiv._timeoutId) {
      clearTimeout(wrapperDiv._timeoutId);
    }

    const toast = wrapperDiv._toast;
    if (!toast) return;

    toast.removeClass('translate-x-0');
    toast.addClass('translate-x-full');
    toast.setOpacity(0);

    setTimeout(() => {
      if (wrapperDiv._originalHeight) {
        wrapperDiv.setStyle('height', [wrapperDiv._originalHeight]);
      }

      wrapperDiv.dom.offsetHeight;

      wrapperDiv.setStyle('height', ['0px']);
      wrapperDiv.setStyle('marginBottom', ['0px']);
      wrapperDiv.setStyle('marginTop', ['0px']);
    }, 100);

    setTimeout(() => {
      if (wrapperDiv.dom && wrapperDiv.dom.parentNode === this.dom) {
        this.remove(wrapperDiv);
        this.toasts = this.toasts.filter(t => t !== wrapperDiv);
      }
    }, 600);
  }

  dismissAll() {
    [...this.toasts].forEach(wrapperDiv => this.dismiss(wrapperDiv));
  }
}

const toast = new Toast();
export default toast;
